import React__default, { useRef, useState, useCallback, useEffect } from 'react';
import { useStyles } from '@sravni/react-utils';
import Queue from '@sravni/utils/lib/queue';
import modules_bbe8aac2 from '../styles/LogoCarousel.module.less.js';
import { LogoCarouselItem } from './LogoCarouselItem.js';

let queue = [];
try {
    queue = new Queue({ debug: false });
}
catch (e) {
    /** */
}
const getClickOffset = (e) => {
    if (e.nativeEvent instanceof TouchEvent) {
        return e.nativeEvent.touches[0].clientX;
    }
    if (e.nativeEvent instanceof MouseEvent) {
        return e.nativeEvent.clientX;
    }
    return 0;
};
/**
 * @description LogoCarousel component. The Logotype slider primarily used for landing pages
 *
 * @component
 * @example
 * ```jsx
 * <LogoCarousel items={items} />
 * ```
 */
const LogoCarousel = React__default.forwardRef((props, ref) => {
    const { className, items, classNameItem, speed = 1, ...restProps } = props;
    const cx = useStyles(modules_bbe8aac2);
    const tracksRef = useRef({
        length: 0,
        items: [],
    });
    const containerRef = useRef(null);
    const animationRef = useRef(0);
    const positionRef = useRef(0);
    const dragOffsetRef = useRef(0);
    const startTimeRef = useRef(0);
    const trackWidthRef = useRef(0);
    const tracksCountRef = useRef(1);
    const [tracksCount, setTrackCount] = useState(1);
    const isDragging = useRef(false);
    const [isRenderDragging, setIsRenderDragging] = useState(false);
    const tracks = Array(tracksCount).fill(null);
    const getTrackWidth = useCallback(() => {
        const track = tracksRef.current.items[0];
        if (track !== undefined && track !== null) {
            return track.clientWidth;
        }
        return 0;
    }, []);
    const getTracksCount = useCallback(() => {
        if (containerRef.current === null || trackWidthRef.current === 0) {
            return 1;
        }
        const tracksTotalAmount = 5;
        return Math.ceil(containerRef.current.clientWidth / trackWidthRef.current) + tracksTotalAmount;
    }, []);
    const setPosition = useCallback((offset) => {
        const trackWidth = trackWidthRef.current;
        positionRef.current = offset;
        for (let i = 0; i < tracksRef.current.length; i++) {
            const track = tracksRef.current.items[i];
            const trackOffset = offset >= trackWidth * (i + 1) ? trackWidth * (tracksCountRef.current - i) - offset : -offset + trackWidth * i;
            if (track) {
                track.style.transform = `translateX(${trackOffset}px)`;
            }
        }
    }, []);
    const animate = (timestamp) => {
        const timeToReposition = 16;
        const maxTimeToReposition = 50;
        const minTimeToReposition = 50;
        const speedMultiplier = 40;
        try {
            let timePassed = timestamp - startTimeRef.current;
            if (timePassed >= timeToReposition) {
                if (timePassed > maxTimeToReposition) {
                    timePassed = minTimeToReposition;
                }
                const position = (positionRef.current + (timePassed / speedMultiplier) * speed) % trackWidthRef.current;
                startTimeRef.current = timestamp;
                if (position !== 0 && !Number.isNaN(position)) {
                    setPosition(position);
                }
            }
            animationRef.current = requestAnimationFrame(animate);
        }
        catch (e) {
            console.warn('Animation Error');
        }
    };
    const onActionStart = useCallback((e) => {
        setIsRenderDragging(true);
        isDragging.current = true;
        dragOffsetRef.current = getClickOffset(e) + positionRef.current;
    }, []);
    const onMove = useCallback((mouseOffset) => {
        if (!isDragging.current) {
            return;
        }
        const trackWidth = trackWidthRef.current;
        let position = dragOffsetRef.current - mouseOffset;
        if (position < 0) {
            position += trackWidth;
        }
        else if (position > tracksCountRef.current * trackWidth) {
            position %= tracksCountRef.current * trackWidth;
        }
        setPosition(position);
    }, [setPosition]);
    const onMouseMove = useCallback((e) => onMove(e.clientX), [onMove]);
    const onTouchMove = useCallback((e) => onMove(e.touches[0].clientX), [onMove]);
    const onActionEnd = useCallback(() => {
        setIsRenderDragging(false);
        isDragging.current = false;
    }, []);
    const trackItemRef = (e, i) => {
        tracksRef.current.length = tracks.length;
        tracksRef.current.items[i] = e;
    };
    useEffect(() => {
        if (isRenderDragging) {
            return;
        }
        queue.push(() => {
            animationRef.current = requestAnimationFrame(animate);
        });
        return () => {
            cancelAnimationFrame(animationRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRenderDragging, animationRef]);
    useEffect(() => {
        const queueUpdateTime = 500;
        let tmp = undefined;
        queue.push(() => {
            tmp = setInterval(() => {
                const count = getTracksCount();
                trackWidthRef.current = getTrackWidth();
                if (tracksCountRef.current !== count) {
                    tracksCountRef.current = count;
                    setTrackCount(count);
                }
            }, queueUpdateTime);
        });
        window.addEventListener('mouseup', onActionEnd);
        window.addEventListener('touchend', onActionEnd);
        window.addEventListener('touchcancel', onActionEnd);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('touchmove', onTouchMove);
        return () => {
            window.removeEventListener('mouseup', onActionEnd);
            window.removeEventListener('touchend', onActionEnd);
            window.removeEventListener('touchcancel', onActionEnd);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('touchmove', onTouchMove);
            if (tmp) {
                clearInterval(tmp);
            }
        };
    }, [
        tracksCountRef,
        setTrackCount,
        trackWidthRef,
        getTracksCount,
        getTrackWidth,
        onActionEnd,
        onMouseMove,
        onTouchMove,
    ]);
    if (!Array.isArray(items) || items.length === 0) {
        return null;
    }
    return (React__default.createElement("div", { ref: ref, "data-qa": 'LogoCarousel', className: cx(className, modules_bbe8aac2.container), ...restProps },
        React__default.createElement("div", { className: modules_bbe8aac2.logoCarousel, "data-qa": 'LogoCarouselInner', onMouseDown: onActionStart, onTouchStart: onActionStart, style: { cursor: isRenderDragging ? 'grabbing' : 'grab' }, ref: containerRef }, tracks.map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        React__default.createElement("div", { className: modules_bbe8aac2.tracks, key: `logobox-track-${i}` },
            React__default.createElement("div", { className: modules_bbe8aac2['tracks--item'], ref: (e) => trackItemRef(e, i) }, items.map((logo) => (React__default.createElement(LogoCarouselItem, { className: classNameItem, key: logo.url, ...logo }))))))))));
});
const LogoCarouselMemo = React__default.memo(LogoCarousel);

export { LogoCarouselMemo as LogoCarousel };
//# sourceMappingURL=LogoCarousel.js.map
